import { useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

import { Logo } from 'components';
import { ROLE_LABEL, ROLES } from 'config/roles';
import NavList from './NavList';
import { CloseIcon } from './icons';

/**
 * MobileSidebar — the responsive drawer that replaces the fixed rail below the
 * `md` breakpoint. A dimmed backdrop fades in while the white panel springs in
 * from the left; both unmount cleanly via AnimatePresence. Body
 * scroll is locked while open, and choosing a route closes the drawer.
 *
 * @param {boolean}  open     Whether the drawer is visible.
 * @param {Function} onClose  Dismiss the drawer.
 * @param {Array}    items    Role nav entries.
 * @param {string}   role     Role key (workspace label).
 */
export default function MobileSidebar({ open, onClose, items, role }) {
  // Lock scroll + close on Escape while the drawer is open.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === 'Escape' && onClose();
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-50 md:hidden" role="dialog" aria-modal="true" aria-label="Navigation">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
          />

          {/* Panel */}
          <motion.aside
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ type: 'spring', stiffness: 360, damping: 36 }}
            className="absolute inset-y-0 left-0 flex w-[82%] max-w-xs flex-col border-r border-brand-100/60 bg-white shadow-elevated"
          >
            <div className="flex h-16 items-center justify-between border-b border-brand-50 px-4">
              <Logo size="sm" />
              <button
                type="button"
                onClick={onClose}
                aria-label="Close navigation"
                className="flex h-9 w-9 items-center justify-center rounded-xl text-slate-500 transition-colors hover:bg-brand-50 hover:text-brand-700"
              >
                <CloseIcon aria-hidden />
              </button>
            </div>

            <div className="border-b border-brand-50 px-4 py-3">
              <span className="text-2xs font-bold uppercase tracking-[0.18em] text-brand-600/70">
                {ROLE_LABEL[role] || 'Member'} workspace
              </span>
            </div>

            <div className="flex-1 overflow-y-auto px-3 py-3">
              <NavList
                items={items}
                layoutId="drawer-pill"
                onNavigate={onClose}
                tone={role === ROLES.STUDENT ? 'soft' : 'solid'}
              />
            </div>
          </motion.aside>
        </div>
      )}
    </AnimatePresence>
  );
}
